import React, { useCallback, useEffect, useState } from 'react';

import { ProfileData } from 'hooks/useAuth';
import { useToast } from 'hooks/useToast';

// import { PURSUIT_TIME_SLOTS } from 'libs/constants';
import { checkImageUrl } from 'libs/utils';

import { LeadData } from 'components/layout/PursuitApplication';
import { PursuitProfileRawData } from 'components/layout/ProfileContainer';
import { Avatar } from 'components/ui/Avatar';
import { UploadImage } from 'components/ui/UploadImage';
import { TextInput } from 'components/ui/TextInput';
import { Button } from 'components/ui/Button';
import { RequiredAsterisk } from 'components/ui/RequiredAsterisk';

import { Fonts, spacing } from 'styles/theme';
import {
    Bumper,
    CardBottomButtonWrapper,
    Checkbox,
    CheckboxWrapper,
    HorizontalFlexWrapper,
    NestedCard,
} from 'styles/shared';

interface ProfileEditorProps {
    userProfile: ProfileData;
    leadData: LeadData;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    parsedProfileRawData: Record<string, any>;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    parsedLeadMetadata: Record<string, any>;
    onCancel: () => void;
    onSaveAndPublish: (
        phoneNumber: string,
        fullName: string,
        linkedIn: string,
        pursuitProfileRawData: PursuitProfileRawData,
        selectedFile: File | null,
        other: string
    ) => void;
    isSaving: boolean;
    isProfileCreation: boolean;
}

export const ProfileEditor = ({
    userProfile,
    leadData,
    parsedProfileRawData,
    parsedLeadMetadata,
    onCancel,
    onSaveAndPublish,
    isSaving,
    isProfileCreation,
}: ProfileEditorProps) => {
    const { showErrorToast } = useToast();

    const [profileImageUrl] = useState<string>(
        parsedProfileRawData?.profile_image_url == null ||
            parsedProfileRawData?.profile_image_url === ''
            ? parsedLeadMetadata?.profile_image_url ?? ''
            : parsedProfileRawData?.profile_image_url ?? ''
    );
    const [isImageValid, setIsImageValid] = useState<boolean>(false);

    const [shouldUseProfileImage, setShouldUseProfileImage] = useState<boolean>(
        parsedProfileRawData?.profile_image_url !== ''
    );
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [selectedPreviewUrl, setSelectedPreviewUrl] = useState<string | null>(
        null
    );
    const [fullName, setFullName] = useState<string>(
        userProfile?.full_name ?? ''
    );
    const [location, setLocation] = useState<string>(
        parsedProfileRawData?.location ?? leadData?.location ?? ''
    );
    const [phoneNumber, setPhoneNumber] = useState<string>(
        parsedLeadMetadata?.phone_number ?? ''
    );
    const [birthday, setBirthday] = useState<string>(
        parsedProfileRawData?.birthday ?? ''
    );
    const [headline, setHeadline] = useState<string>(
        parsedProfileRawData?.headline ?? leadData?.title ?? ''
    );
    const [company, setCompany] = useState<string>(
        parsedProfileRawData?.company ?? leadData?.company ?? ''
    );
    const [linkedIn, setLinkedIn] = useState<string>(
        userProfile?.linkedin ?? ''
    );
    const [instagram, setInstagram] = useState<string>(
        parsedProfileRawData?.instagram ?? parsedLeadMetadata?.instagram ?? ''
    );
    const [zoneOfGenius, setZoneOfGenius] = useState<string>(
        parsedProfileRawData?.zone_of_genius ?? ''
    );
    const [interests, setInterests] = useState<string>(
        parsedProfileRawData?.interests ?? ''
    );
    const [children, setChildren] = useState<string>(
        parsedProfileRawData?.children ?? parsedLeadMetadata?.children ?? ''
    );
    const [otherSuggestedTime, setOtherSuggestedTime] = useState<string>('');

    useEffect(() => {
        const validateImage = async () => {
            if (profileImageUrl) {
                const valid = await checkImageUrl(profileImageUrl);
                setIsImageValid(valid);
            } else {
                setIsImageValid(false);
            }
        };
        validateImage();
    }, [profileImageUrl]);

    const handleSelectImage = useCallback(
        (file: File) => {
            setSelectedFile(file);
            if (selectedPreviewUrl) {
                URL.revokeObjectURL(selectedPreviewUrl);
            }
            setSelectedPreviewUrl(URL.createObjectURL(file));
            setShouldUseProfileImage(true);
        },
        [selectedPreviewUrl]
    );

    useEffect(() => {
        return () => {
            if (selectedPreviewUrl) {
                URL.revokeObjectURL(selectedPreviewUrl);
            }
        };
    }, [selectedPreviewUrl]);

    const onToggleUseProfileImage = useCallback(
        (event: React.FormEvent<HTMLInputElement>) => {
            const target = event.target as HTMLInputElement;
            setShouldUseProfileImage(target.checked);
        },
        []
    );

    const onChangeFullName = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            const target = event.target as HTMLInputElement;
            setFullName(target.value);
        },
        []
    );

    const onChangePhoneNumber = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            const target = event.target as HTMLInputElement;
            setPhoneNumber(target.value);
        },
        []
    );

    const onChangeBirthday = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            const target = event.target as HTMLInputElement;
            setBirthday(target.value);
        },
        []
    );

    const onChangeHeadline = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            const target = event.target as HTMLInputElement;
            setHeadline(target.value);
        },
        []
    );

    const onChangeCompany = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            const target = event.target as HTMLInputElement;
            setCompany(target.value);
        },
        []
    );

    const onChangeLocation = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            const target = event.target as HTMLInputElement;
            setLocation(target.value);
        },
        []
    );

    const onChangeLinkedIn = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            const target = event.target as HTMLInputElement;
            setLinkedIn(target.value);
        },
        []
    );

    const onChangeInstagram = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            const target = event.target as HTMLInputElement;
            setInstagram(target.value);
        },
        []
    );

    const onChangeZoneOfGenius = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            const target = event.target as HTMLInputElement;
            setZoneOfGenius(target.value);
        },
        []
    );

    const onChangeInterests = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            const target = event.target as HTMLInputElement;
            setInterests(target.value);
        },
        []
    );

    const onChangeChildren = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            const target = event.target as HTMLInputElement;
            setChildren(target.value);
        },
        []
    );

    const onOtherSuggestedTimeInput = useCallback(
        (event: React.FormEvent<HTMLInputElement>) => {
            const target = event.target as HTMLInputElement;
            setOtherSuggestedTime(target.value);
        },
        []
    );

    const onSubmit = useCallback(
        (event: React.FormEvent<HTMLFormElement>) => {
            event.preventDefault();

            if (
                isProfileCreation &&
                (userProfile.type === 'Pursuit Retreat' ||
                    userProfile.type === 'Pursuit Digital') &&
                otherSuggestedTime === ''
            ) {
                showErrorToast(
                    'Please enter a response about your weekly availability before moving forward.'
                );
                return;
            }

            onSaveAndPublish(
                phoneNumber,
                fullName,
                linkedIn,
                {
                    profile_image_url: shouldUseProfileImage
                        ? profileImageUrl
                        : '',
                    location,
                    birthday,
                    company,
                    headline,
                    instagram,
                    zone_of_genius: zoneOfGenius,
                    interests,
                    children,
                },
                shouldUseProfileImage ? selectedFile : null,
                otherSuggestedTime
            );
        },
        [
            birthday,
            children,
            company,
            fullName,
            headline,
            instagram,
            interests,
            isProfileCreation,
            linkedIn,
            location,
            onSaveAndPublish,
            otherSuggestedTime,
            phoneNumber,
            profileImageUrl,
            selectedFile,
            shouldUseProfileImage,
            showErrorToast,
            userProfile.type,
            zoneOfGenius,
        ]
    );

    return (
        <>
            <Fonts.Heading7>{`Edit Your Profile`}</Fonts.Heading7>
            <Bumper />
            {parsedProfileRawData?.is_published ? null : (
                <>
                    <Fonts.Medium>{`This information will be used to create your profile page, which will be shared with other members of The Pursuit community. Until you've saved and published this, only your name will be displayed in The Pursuit member directory.`}</Fonts.Medium>
                    <Bumper />
                </>
            )}
            <form onSubmit={onSubmit}>
                <HorizontalFlexWrapper>
                    {(profileImageUrl === '' || !isImageValid) &&
                    selectedPreviewUrl == null ? null : (
                        <>
                            <Avatar
                                fullName={fullName}
                                imageUrl={selectedPreviewUrl ?? profileImageUrl}
                                size="large"
                            />
                            <Bumper $minWidth={spacing.medium} />
                        </>
                    )}
                    <UploadImage
                        category="profile"
                        onSelectImage={handleSelectImage}
                        disabled={isSaving}
                    />
                </HorizontalFlexWrapper>
                {(profileImageUrl === '' || !isImageValid) &&
                selectedPreviewUrl == null ? null : (
                    <>
                        <Bumper />
                        <CheckboxWrapper>
                            <Checkbox
                                type="checkbox"
                                name="share"
                                onChange={onToggleUseProfileImage}
                                checked={shouldUseProfileImage}
                            />
                            <Fonts.Small>
                                {`Checking this box indicates that you'd like to use this profile image in the member directory`}
                            </Fonts.Small>
                        </CheckboxWrapper>
                    </>
                )}

                <Bumper $minHeight={spacing.xLarge} />
                <TextInput
                    value={fullName}
                    label="Full name"
                    onChange={onChangeFullName}
                    required
                />
                <Bumper />
                <TextInput
                    value={phoneNumber}
                    label="Phone number"
                    subLabel="For communication purposes only, and will not be displayed on your profile"
                    onChange={onChangePhoneNumber}
                    required
                />
                <Bumper />
                <TextInput
                    value={birthday}
                    label="Birthday"
                    subLabel="This is optional, and may be displayed when connecting you with other members if provided"
                    onChange={onChangeBirthday}
                    type="date"
                />
                <Bumper />
                <TextInput
                    value={headline}
                    label="Headline"
                    onChange={onChangeHeadline}
                    required
                />
                <Bumper />
                <TextInput
                    value={company}
                    label="Company"
                    onChange={onChangeCompany}
                    required
                />
                <Bumper />
                <TextInput
                    value={location}
                    label="Location"
                    onChange={onChangeLocation}
                    required
                />
                <Bumper />
                <TextInput
                    value={linkedIn}
                    label="LinkedIn URL"
                    onChange={onChangeLinkedIn}
                    required
                />
                <Bumper />
                <TextInput
                    value={instagram}
                    label="Instagram handle"
                    onChange={onChangeInstagram}
                />
                <Bumper />
                <TextInput
                    value={zoneOfGenius}
                    label="Zone of Genius"
                    placeholder="Eg. I have a unique ability to take complex, technical ideas and explain them in a way that’s simple, engaging, and inspiring."
                    onChange={onChangeZoneOfGenius}
                    isTextArea
                    required
                />
                <Bumper />
                <TextInput
                    value={interests}
                    label="Passions, hobbies, and interests"
                    onChange={onChangeInterests}
                    isTextArea
                    required
                />
                <Bumper />
                <TextInput
                    value={children}
                    label="Children"
                    subLabel="Ages are the only required detail, but feel free to share names and anything else you’d like to include"
                    onChange={onChangeChildren}
                    isTextArea
                    required
                />
                <Bumper />
                {isProfileCreation &&
                (userProfile.type === 'Pursuit Retreat' ||
                    userProfile.type === 'Pursuit Digital') ? (
                    <>
                        <NestedCard>
                            <Fonts.Small>
                                {`We'll use the following information to curate your group and coach around a time that works best for you. This is for internal purposes only, and won't be shared on your profile.`}
                            </Fonts.Small>
                            <Bumper $minHeight={spacing.xSmall} />
                            <Fonts.Small>
                                {`Sessions last for 75 minutes and will be at the same time and day of the week every month (except for extenuating circumstances, which we will share with as much advance notice as possible).`}
                            </Fonts.Small>
                            <Bumper $minHeight={spacing.xSmall} />
                            <Fonts.Small>
                                {`We typically schedule sessions on Tuesdays, Wednesdays, or Thursdays at 9am, 12pm, or 3pm PST. Is there any day of the week and time that you absolutely `}
                                <Fonts.Small
                                    $isOpenSansSemiBold
                                >{`cannot`}</Fonts.Small>
                                {` meet with your group during?`}
                                <RequiredAsterisk />
                            </Fonts.Small>
                            <Bumper $minHeight={spacing.xSmall} />
                            <TextInput
                                type="text"
                                id="otherSuggestedTime"
                                name="otherSuggestedTime"
                                value={otherSuggestedTime}
                                onInput={onOtherSuggestedTimeInput}
                                required
                            />
                        </NestedCard>
                        <Bumper $minHeight={spacing.xLarge} />
                    </>
                ) : null}
                <CardBottomButtonWrapper $backgroundColor="transparent">
                    <HorizontalFlexWrapper>
                        <Button
                            onClick={onCancel}
                            $style="outline"
                        >{`Cancel`}</Button>
                        <Bumper $minWidth={spacing.small} />
                        <Button
                            type="submit"
                            $isLoading={isSaving}
                        >{`Save and Publish`}</Button>
                    </HorizontalFlexWrapper>
                </CardBottomButtonWrapper>
            </form>
        </>
    );
};
